// Jobs Index Bundle - Optimized bundle for jobs#index page  
// This bundle includes only the components needed for job search and listing

import ReactOnRails from "react-on-rails";
import "@rails/actioncable";
import { wrapWithErrorBoundary } from "@/shared/components/error";

// Main Jobs search component (used in jobs#index) - Now using organized components
import { Jobs, JobList, JobFilters, JobSearchJob } from '@/domains/jobs';
import JobTags from '@/domains/jobs/components/JobTags';
import JobSkills from '@/domains/jobs/components/JobSkills';
import { Pagination } from '@/shared/index';

// Supporting components needed for job pages - Now using organized components
import { SBChat, SBMobileBanner } from '@/domains/messaging';
import { globalComponents } from '@/shared/registerGlobalComponents';

// AI Assistant (used in layouts/application.html.erb for practice users)

// Register only the components needed for jobs#index
ReactOnRails.register(wrapWithErrorBoundary({
  ...globalComponents,
    Jobs,
    JobList,
    JobFilters,
    Job: JobSearchJob, // Using JobSearchJob component
    JobTags,
    JobSkills,
    Pagination,
    SBChat,
    SBMobileBanner,
}));

// Jobs index page specific optimizations
document.addEventListener('DOMContentLoaded', function () {
    // Job search performance optimizations
    const jobSearchForm = document.querySelector('form[action*="/search"]');
    if (jobSearchForm) {
        // Debounce search input to reduce API calls
        const searchInputs = jobSearchForm.querySelectorAll('input[type="text"], select');
        searchInputs.forEach(input => {
            let debounceTimer;
            input.addEventListener('input', function () {
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    // Trigger search after 300ms of no input
                    console.log('Search input debounced:', this.value);
                }, 300);
            });
        });
    }

    // Job card hover effects and animations for fallback jobs
    const jobCards = document.querySelectorAll('.job-list li, .fallback-job-card');
    jobCards.forEach(card => {
        card.addEventListener('mouseenter', function () {
            this.style.transform = 'translateY(-2px)';
            this.style.transition = 'transform 0.2s ease';
            this.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        });

        card.addEventListener('mouseleave', function () {
            this.style.transform = 'translateY(0)';
            this.style.boxShadow = 'initial';
        });
    });

    // Track job search interactions
    const jobSearchContainer = document.querySelector('#jobs-search-container, [data-react-component="Jobs"]');
    if (jobSearchContainer) {
        // Track filter usage
        jobSearchContainer.addEventListener('change', function (event) {
            if (event.target.matches('input[type="checkbox"], select, input[type="radio"]')) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'job_filter_used', {
                        'event_category': 'Jobs Search',
                        'event_label': event.target.name || event.target.className,
                        'filter_type': event.target.type,
                        'filter_value': event.target.value
                    });
                }
            }
        });

        // Track job clicks
        jobSearchContainer.addEventListener('click', function (event) {
            if (event.target.closest('.job-item, .job-card, .fallback-job-card')) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'job_click', {
                        'event_category': 'Jobs Search',
                        'event_label': 'Job Item Click'
                    });
                }
            }
        });

        // Track pagination usage
        jobSearchContainer.addEventListener('click', function (event) {
            if (event.target.closest('.pagination a, .page-link')) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'pagination_click', {
                        'event_category': 'Jobs Search',
                        'event_label': 'Pagination Navigation'
                    });
                }
            }
        });
    }

    // Search filters accessibility improvements
    const filterCheckboxes = document.querySelectorAll('.checkboxes input[type="checkbox"], .job-filters input[type="checkbox"]');
    filterCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', function () {
            const label = document.querySelector(`label[for="${this.id}"]`);
            if (label) {
                if (this.checked) {
                    label.style.fontWeight = 'bold';
                    label.setAttribute('aria-pressed', 'true');
                } else {
                    label.style.fontWeight = 'normal';
                    label.setAttribute('aria-pressed', 'false');
                }
            }
        });
    });

    // Fallback form progressive enhancement
    const fallbackForm = document.querySelector('.fallback-search-form');
    const reactJobsContainer = document.querySelector('[data-react-component="Jobs"]');

    if (fallbackForm && reactJobsContainer) {
        // Hide fallback form when React component loads successfully
        const observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    const hasReactContent = Array.from(mutation.addedNodes).some(node =>
                        node.nodeType === Node.ELEMENT_NODE &&
                        (node.querySelector('.jobs-list, .job-filters') ||
                            node.classList && node.classList.contains('jobs-list'))
                    );
                    if (hasReactContent) {
                        fallbackForm.style.display = 'none';
                        observer.disconnect();
                    }
                }
            });
        });
        observer.observe(reactJobsContainer, { childList: true, subtree: true });

        // Fallback: hide form after 3 seconds if React loads
        setTimeout(() => {
            if (reactJobsContainer.children.length > 0) {
                fallbackForm.style.display = 'none';
            }
        }, 3000);
    }

    // Location autocomplete enhancement for search forms
    const locationInputs = document.querySelectorAll('input[name*="location"], input[name*="city"]');
    locationInputs.forEach(input => {
        let suggestionTimeout;
        input.addEventListener('input', function () {
            clearTimeout(suggestionTimeout);
            suggestionTimeout = setTimeout(() => {
                const value = this.value;
                if (value.length >= 3) {
                    // Could implement location suggestions here
                    console.log('Location search:', value);
                }
            }, 500);
        });
    });

    // Sort options tracking
    const sortSelects = document.querySelectorAll('select[name*="sort"], .sort-dropdown select');
    sortSelects.forEach(select => {
        select.addEventListener('change', function () {
            if (typeof gtag !== 'undefined') {
                gtag('event', 'sort_change', {
                    'event_category': 'Jobs Search',
                    'event_label': 'Sort Order Changed',
                    'sort_value': this.value
                });
            }
        });
    });

    // Mobile search optimizations
    if (window.innerWidth <= 768) {
        // Sticky search filters on mobile
        const filtersContainer = document.querySelector('.job-filters, .filters-container');
        if (filtersContainer) {
            let ticking = false;
            function updateFiltersPosition() {
                const scrollY = window.scrollY;
                if (scrollY > 100) {
                    filtersContainer.classList.add('filters-sticky');
                } else {
                    filtersContainer.classList.remove('filters-sticky');
                }
                ticking = false;
            }

            window.addEventListener('scroll', function () {
                if (!ticking) {
                    requestAnimationFrame(updateFiltersPosition);
                    ticking = true;
                }
            });
        }
    }

    // Performance monitoring for job search
    const jobSearchStart = performance.now();
    window.addEventListener('load', function () {
        const jobSearchEnd = performance.now();
        const loadTime = jobSearchEnd - jobSearchStart;

        if (typeof gtag !== 'undefined' && loadTime > 1000) {
            gtag('event', 'slow_job_search_load', {
                'event_category': 'Performance',
                'event_label': 'Job Search Page Load Time',
                'value': Math.round(loadTime)
            });
        }
    });

    console.log('Jobs index bundle loaded successfully');
});
